// Single source of truth for dispute Status presentation (badge colour, label,
// description) and the Status filter option list. Imported by the /recover/all
// disputes table (AllPage) AND the Analytics Activity stream (activityColumns) so
// a status renders and filters identically everywhere, with no second copy to
// drift. Labels/descriptions are English source strings passed through t() at the
// point of render. 'default' is an antd Badge STATUS (grey dot), not a colour.
// Colours follow the client's status sheet: Open blue, Awaiting review amber, Won
// green, both Lost outcomes one red, Escalated a darker burnt orange, Skipped grey.
// antd Badge/Tag presets are the closest system colours to that palette.
export const STATUS_TAG_COLORS = {
  needs_response: "blue",
  awaiting_review: "orange",
  // A case the merchant never submitted evidence for by choice (low-value rule or Stop
  // fighting). Grey, since there is no outcome to colour.
  skipped: "default",
  // Purple per the client's request, so the two
  // issuer-side waiting states stay visually distinct from each other.
  pending_decision: "purple",
  // Inquiry-only: the inquiry was not resolved and is on its way to becoming a formal
  // dispute. Volcano = the sheet's burnt orange, darker than Awaiting review's amber.
  escalated: "volcano",
  won: "green",
  // One red for both lost outcomes, exactly as the sheet groups them.
  lost_defended: "red",
  lost_undefended: "red"
}

export const STATUS_LABELS = {
  needs_response: "Open",
  awaiting_review: "Awaiting review",
  skipped: "Skipped",
  pending_decision: "Pending decision",
  escalated: "Escalated",
  won: "Won",
  // Lost split by whether evidence was ever submitted: defended means fought and lost,
  // undefended means the deadline passed with nothing sent (distinct from Skipped, which
  // is a deliberate choice not to fight).
  lost_defended: "Lost defended",
  lost_undefended: "Lost undefended"
}

// The two lost outcomes, for every "is this lost?" check (timeline, analytics, KPI
// buckets). One constant so no comparison site can miss a half of the split.
export const LOST_STATUSES = ["lost_defended", "lost_undefended"]

export const STATUS_DESCRIPTIONS = {
  needs_response: "A new chargeback, scheduled to be submitted automatically.",
  awaiting_review:
    "A new chargeback that won't be submitted automatically: a repeat disputer, or one of your rules applies. Review and submit it yourself.",
  skipped:
    "No evidence was submitted: it matched your 'Ignore low-value disputes' rule, or you chose to stop fighting it.",
  pending_decision: "Evidence was submitted to the issuer. Awaiting their decision.",
  escalated:
    "The inquiry was not resolved and is escalating: the issuer is turning it into a formal dispute.",
  won: "The issuer ruled in your favour. Funds recovered.",
  lost_defended:
    "The issuer ruled against you after your evidence was submitted. No success fee was charged.",
  lost_undefended: "Lost with no evidence submitted: the response deadline passed unfought."
}

// Filter options, in the same order as the agreed status table: the two live states, the
// skipped bucket, the submitted state, then the outcomes. Passed with sortDisabled so this
// order survives the dropdown's default alphabetical sort. Every status a dispute can hold
// is listed here, so nothing can show in the table without being filterable.
export const STATUS_FILTER_ORDER = [
  "needs_response",
  "awaiting_review",
  "skipped",
  "pending_decision",
  "escalated",
  "won",
  "lost_defended",
  "lost_undefended"
]
export const STATUS_DATA = STATUS_FILTER_ORDER.map((id) => ({ id, name: STATUS_LABELS[id] }))

// Case type: a formal chargeback vs a pre-dispute inquiry (the issuer asking questions
// before deciding whether to file). Shared by the table column, the Type filter, the
// detail page and the assignee rules, so the label set cannot drift.
export const TYPE_LABELS = {
  dispute: "Dispute",
  inquiry: "Inquiry"
}
export const TYPE_DATA = Object.entries(TYPE_LABELS).map(([id, name]) => ({ id, name }))

// Disputes we never fought, so nothing was submitted on the merchant's behalf. Kept here
// with the rest of the status vocabulary rather than in a page module — the list, the
// summary cards and the detail page all need the same answer. (Was in AnalyticsPage.)
export const NOT_FIGHTING = ["skipped"]
